import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { fetchWorkProductDocuments, WorkproductDocument, fetchWorkProductTitle } from "@/api";
import type { RootState } from "../store";

interface WorkProduct_GetDocuments {
    status: "idle" | "loading" | "succeeded" | "failed";
    documents: WorkproductDocument[];
    wpTitle?: string; // Optional title for the work product
    activeVersion?: number; // The active version being displayed
    multipleVersions?: boolean; // Whether multiple versions exist
    error: string | null;
    // Active work product context for chat and other components
    activeContext?: {
        workProductId: number;
        projectId: number;
        featureId?: string;
        userStoryId?: string;
    };
    // Tracks the latest in-flight request to prevent stale responses
    // from overwriting newer selections.
    currentRequestId?: string;
}

const initialState: WorkProduct_GetDocuments = {
    status: "idle",
    documents: [],
    error: null,
    activeContext: undefined,
    currentRequestId: undefined
};

export const getWorkProductDocuments = createAsyncThunk<
    { documents: WorkproductDocument[]; title: string; activeVersion: number; multipleVersions: boolean; context: { workProductId: number; projectId: number; featureId?: string; userStoryId?: string } },
    { user_id: number; project_id: number; workProduct_id: number; version: number; feature_id?: string; user_story_id?: string },
    { state: RootState; rejectValue: string }
>("workProductDocuments/fetchWorkProductDocuments", async ({ user_id, project_id, workProduct_id, version, feature_id, user_story_id }, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const token = state.clientToken.token ?? "";
    if (!token) {
        return rejectWithValue("Token is missing");
    }

    try {
        // Fetch documents and title in parallel
        const [documentsResponse, title] = await Promise.all([
            fetchWorkProductDocuments(user_id, project_id, workProduct_id, version, feature_id || '', token, user_story_id),
            fetchWorkProductTitle(project_id, workProduct_id, token)
        ]);
        return {
            documents: documentsResponse.documents,
            title,
            activeVersion: documentsResponse.active_version,
            multipleVersions: documentsResponse.multiple_versions,
            context: {
                workProductId: workProduct_id,
                projectId: project_id,
                featureId: feature_id,
                userStoryId: user_story_id
            }
        };
    } catch (error: any) {
        console.error("Error fetching work product data:", error);
        return rejectWithValue(error.message ?? "Fetch failed");
    }
});

const workProductDocumentsSlice = createSlice({
    name: "workProductDocuments",
    initialState,
    reducers: {
        resetDocumentsState(state) {
            state.status = "idle";
            state.documents = [];
            state.error = null;
            state.activeVersion = undefined;
            state.multipleVersions = undefined;
            state.activeContext = undefined;
            state.currentRequestId = undefined;
        },
        setActiveContext(state, action: PayloadAction<{ workProductId: number; projectId: number; featureId?: string; userStoryId?: string }>) {
            state.activeContext = action.payload;
        }
    },
    extraReducers: builder => {
        builder
            .addCase(getWorkProductDocuments.pending, (state, action) => {
                state.status = "loading";
                state.error = null;
                state.currentRequestId = action.meta.requestId;
            })
            .addCase(getWorkProductDocuments.fulfilled, (state, action) => {
                // Ignore stale responses from older requests.
                if (state.currentRequestId && state.currentRequestId !== action.meta.requestId) {
                    return;
                }
                state.status = "succeeded";
                state.documents = action.payload.documents;
                state.wpTitle = action.payload.title;
                state.activeVersion = action.payload.activeVersion;
                state.multipleVersions = action.payload.multipleVersions;
                state.activeContext = action.payload.context;
                state.currentRequestId = undefined;
            })
            .addCase(getWorkProductDocuments.rejected, (state, action) => {
                // Ignore stale errors from older requests.
                if (state.currentRequestId && state.currentRequestId !== action.meta.requestId) {
                    return;
                }
                state.status = "failed";
                state.error = action.payload ?? "Failed to fetch documents.";
                state.currentRequestId = undefined;
            });
    }
});

export const { resetDocumentsState, setActiveContext } = workProductDocumentsSlice.actions;
export default workProductDocumentsSlice.reducer;
